import {
  convertMonthsToYearsAndMonths,
  LoanOfferAmortization,
  LoanOfferBaseRate,
  LoanOfferFinancingType,
  LoanOfferFixedBaseRate,
  LoanOfferFrequencyInMonths,
  LoanOfferItem,
  LoanOfferMode,
  LoanOfferPurpose,
  LoanOfferReason,
  LoanOfferTargetType,
  LoanOfferType,
  SecLoansUserInfoResponse,
} from '@shared/sec-loans'
import { Address } from '@ui-common/components/Address'
import { formatDate, formatFinnishCurrencyValue, formatFinnishCurrencyWithSymbol, getLanguage } from '@ui-common/utils'
import { SummaryData } from '@ui-components/form/StepSummaryView'
import { IntlShape } from 'react-intl'

export interface LoanOfferSummaryItem {
  offeredLoanId: string
  title: string
  purpose?: string
  coDebtorName?: string
  targetData: SummaryData[]
  offerData: SummaryData[]
  shouldShowEsisLink: boolean
}

export const getOfferSummaryData = (
  user: SecLoansUserInfoResponse,
  offerReason: LoanOfferReason,
  offerItem: LoanOfferItem,
  intl: IntlShape,
): LoanOfferSummaryItem => {
  const {
    loanOfferTypeTexts,
    referenceRateTexts,
    targetTypeTexts,
    amortizationTypeTexts,
    amortizationDescriptionTexts,
    loanPurposeTexts,
    financingTypeTexts,
  } = getLocalizedEnums(intl.formatMessage)

  const { formatMessage } = intl
  const language = getLanguage()

  const { years, months } = convertMonthsToYearsAndMonths(offerReason.loanPeriodMonths)
  const baseRateDate = formatDate(offerReason.baseRateDate, intl.locale)
  const shouldShowEsisLink = ![LoanOfferType.RenovationLoan, LoanOfferType.ConsumptionLoan].includes(
    offerReason.loanType,
  )

  const targetData: SummaryData[] = [
    ...(offerReason.targetType
      ? [
          {
            heading: formatMessage({ id: 'loan-offer-target-type-label' }),
            value: targetTypeTexts[offerReason.targetType],
          },
        ]
      : []),
    ...(offerReason.targetAddress
      ? [
          {
            heading: formatMessage({ id: 'loan-offer-target-address-label' }),
            value: <Address address={offerReason.targetAddress} />,
          },
        ]
      : []),
  ]

  const getReferenceRateAndMargin = () => {
    const isFixedInterestRate = Object.values(LoanOfferFixedBaseRate).includes(
      offerReason.baseRateType as LoanOfferFixedBaseRate,
    )

    if (isFixedInterestRate && offerReason.baseRateAfterFixed) {
      return [
        {
          heading: formatMessage({ id: 'sec-loans-reference-rate-info-header' }),
          value: referenceRateTexts[offerReason.baseRateType],
          childData: [
            {
              heading: formatMessage({ id: 'loan-offer-reference-rate-after-fixed-label' }),
              value: referenceRateTexts[offerReason.baseRateAfterFixed],
            },
            {
              heading: formatMessage({ id: 'loan-offer-margin-after-fixed-label' }),
              value: `+${formatFinnishCurrencyValue(offerReason.margin)} %`,
            },
          ],
        },
      ]
    }

    const isManualOffer = offerItem.loanOfferMode === LoanOfferMode.Manual

    return [
      {
        heading: formatMessage({ id: 'sec-loans-reference-rate-info-header' }),
        description: formatMessage({ id: 'loan-offer-reference-rate-description' }),
        value: (
          <>
            {referenceRateTexts[offerReason.baseRateType]}
            <br />
            {`+${formatFinnishCurrencyValue(offerReason.baseRateValue)} % (${baseRateDate})`}
          </>
        ),
      },
      {
        heading: isManualOffer
          ? formatMessage({ id: 'loan-offer-margin-label' })
          : formatMessage({ id: 'loan-offer-preliminary-margin-label' }),
        description: isManualOffer ? undefined : formatMessage({ id: 'loan-offer-margin-description' }),
        value: `+${formatFinnishCurrencyValue(offerReason.margin)} %`,
      },
    ]
  }

  const offerData: SummaryData[] = [
    ...(offerReason.financingType !== LoanOfferFinancingType.Regular
      ? [
          {
            heading: formatMessage({ id: 'loan-offer-loan-type-label' }),
            value: financingTypeTexts[offerReason.financingType],
          },
        ]
      : []),
    {
      heading: formatMessage({ id: 'loan-offer-loan-amount-label' }),
      value: `${formatFinnishCurrencyWithSymbol(offerReason.amount)}`,
    },
    {
      heading: formatMessage({ id: 'sec-loans-payment-plan-info-repayment-method' }),
      value: amortizationTypeTexts[offerReason.amortization],
      description: amortizationDescriptionTexts[offerReason.amortization],
      childData: [
        {
          heading: formatMessage({ id: 'loan-offer-payment-amount-label' }),
          value: formatFinnishCurrencyWithSymbol(
            offerReason.amortization === LoanOfferAmortization.ConstantDuration
              ? offerReason.installmentAmount
              : offerReason.firstPaymentAmount,
          ),
        },
        {
          heading: formatMessage({ id: 'loan-offer-installment-interest-amount-label' }),
          value: `${formatFinnishCurrencyWithSymbol(offerReason.installmentInterestAmount)}`,
        },
        ...(offerReason.amortization === LoanOfferAmortization.ConstantDuration
          ? [
              {
                heading: formatMessage({ id: 'loan-offer-first-payment-amount-label' }),
                value: formatFinnishCurrencyWithSymbol(offerReason.firstPaymentAmount),
              },
            ]
          : []),
      ],
    },
    ...(offerReason.paymentFrequency !== LoanOfferFrequencyInMonths.Monthly
      ? [
          {
            heading: formatMessage({ id: 'loan-offer-payment-frequency-label' }),
            value: `${offerReason.paymentFrequency} ${formatMessage({ id: 'common-month-abbreviation' })}`,
          },
        ]
      : []),
    ...(offerReason.interestFrequency !== LoanOfferFrequencyInMonths.Monthly
      ? [
          {
            heading: formatMessage({ id: 'loan-offer-interest-frequency-label' }),
            value: `${offerReason.interestFrequency} ${formatMessage({ id: 'common-month-abbreviation' })}`,
          },
        ]
      : []),
    {
      heading: formatMessage({ id: 'sec-loans-payment-plan-loan-term-label' }),
      value: formatMessage({ id: 'sec-loans-payment-plan-calculated-estimated-loan-time' }, { years, months }),
    },
    {
      heading: formatMessage({ id: 'loan-offer-opening-fee-label' }),
      value: `${formatFinnishCurrencyWithSymbol(offerReason.openingFee)}`,
    },
    ...(offerReason.feesRelatedToOfficialFees
      ? [
          {
            heading: formatMessage({ id: 'loan-offer-fees-related-to-official-fees' }),
            value: `${formatFinnishCurrencyWithSymbol(offerReason.feesRelatedToOfficialFees)}`,
          },
        ]
      : []),
    ...(offerItem.sHousingGuaranteeFee
      ? [
          {
            heading: formatMessage({ id: 'loan-offer-s-housing-guarantee-fee-label' }),
            value: `${formatFinnishCurrencyWithSymbol(offerItem.sHousingGuaranteeFee)}`,
          },
        ]
      : []),
    {
      heading: formatMessage({ id: 'loan-offer-installment-free-period-label' }),
      value: `${offerReason.installmentFreePeriodMonths} ${formatMessage({ id: 'common-month-abbreviation' })}`,
    },
    {
      heading: formatMessage({ id: 'loan-offer-debitfee-label' }),
      description: user.isBankEmployee ? formatMessage({ id: 'loan-offer-debitfee-description' }) : undefined,
      value: formatFinnishCurrencyWithSymbol(offerReason.debitFee),
    },
    ...getReferenceRateAndMargin(),
    {
      heading: formatMessage({ id: 'loan-offer-total-interest-rate-label' }),
      value: `+${formatFinnishCurrencyValue(offerReason.totalInterestRate)} % (${baseRateDate})`,
    },
    {
      heading: formatMessage({ id: 'loan-offer-effective-annual-interest-rate-label' }),
      value: `+${formatFinnishCurrencyValue(offerReason.effectiveAnnualInterestRate)} % (${baseRateDate})`,
    },
    {
      heading: formatMessage({ id: 'sec-loans-insurance-info-header' }),
      value: offerReason.protections.map((item) => item.description[language]).join(', '),
    },
    {
      heading: formatMessage({ id: 'loan-offer-guarantee-label' }),
      value: formatMessage({ id: 'loan-offer-guarantee-value' }),
    },
  ]

  let purpose: string | undefined

  if (offerReason.loanType === LoanOfferType.ConsumptionLoan && offerReason.loanPurpose) {
    purpose = loanPurposeTexts[offerReason.loanPurpose]
  } else if (offerReason.isLoanTransfer) {
    purpose = formatMessage({ id: 'sec-loans-loan-purpose-loan-transfer-to-sbank' })
  } else if (
    offerReason.loanType !== LoanOfferType.RenovationLoan &&
    offerReason.loanType !== LoanOfferType.ConstructionLoan
  ) {
    purpose = formatMessage({ id: 'sec-loans-loan-purpose-buy' })
  }

  return {
    offeredLoanId: offerReason.offeredLoanId,
    targetData,
    offerData,
    title: loanOfferTypeTexts[offerReason.loanType],
    purpose,
    coDebtorName: offerReason.coDebtorName,
    shouldShowEsisLink,
  }
}

const getLocalizedEnums = (formatMessage: IntlShape['formatMessage']) => {
  const loanOfferTypeTexts: Record<LoanOfferType, string> = {
    [LoanOfferType.ConstructionLoan]: formatMessage({ id: 'loan-offer-loan-offer-type-construction-loan-label' }),
    [LoanOfferType.ConsumptionLoan]: formatMessage({ id: 'loan-offer-loan-offer-type-consumption-loan-label' }),
    [LoanOfferType.CottageLoan]: formatMessage({ id: 'loan-offer-loan-offer-type-cottage-loan-label' }),
    [LoanOfferType.HousingLoan]: formatMessage({ id: 'loan-offer-loan-offer-type-housing-loan-label' }),
    [LoanOfferType.InvestmentLoan]: formatMessage({ id: 'loan-offer-loan-offer-type-investment-loan-label' }),
    [LoanOfferType.RenovationLoan]: formatMessage({ id: 'loan-offer-loan-offer-type-renovation-loan-label' }),
  }

  const referenceRateTexts: Record<LoanOfferBaseRate, string> = {
    [LoanOfferBaseRate.Euribor1]: formatMessage({ id: 'sec-loans-reference-rate-euribor-1' }),
    [LoanOfferBaseRate.Euribor3]: formatMessage({ id: 'sec-loans-reference-rate-euribor-3' }),
    [LoanOfferBaseRate.Euribor6]: formatMessage({ id: 'sec-loans-reference-rate-euribor-6' }),
    [LoanOfferBaseRate.Euribor12]: formatMessage({ id: 'sec-loans-reference-rate-euribor-12' }),
    [LoanOfferBaseRate.Fixed3]: formatMessage({ id: 'sec-loans-reference-rate-fixed-3' }),
    [LoanOfferBaseRate.Fixed5]: formatMessage({ id: 'sec-loans-reference-rate-fixed-5' }),
    [LoanOfferBaseRate.Fixed10]: formatMessage({ id: 'sec-loans-reference-rate-fixed-10' }),
    [LoanOfferBaseRate.Prime]: formatMessage({ id: 'sec-loans-reference-rate-fixed' }),
  }

  const targetTypeTexts: Record<LoanOfferTargetType, string> = {
    [LoanOfferTargetType.BasicImprovement]: formatMessage({ id: 'loan-offer-loan-offer-type-renovation-loan-label' }),
    [LoanOfferTargetType.HolidayShare]: formatMessage({ id: 'loan-offer-property-type-holiday-share' }),
    [LoanOfferTargetType.HousingCooperative]: formatMessage({ id: 'sec-loans-property-type-housing-stock' }),
    [LoanOfferTargetType.PartOwnership]: formatMessage({ id: 'sec-loans-property-type-part-ownership' }),
    [LoanOfferTargetType.RealEstate]: formatMessage({ id: 'sec-loans-property-type-residential' }),
    [LoanOfferTargetType.RightOfOccupancy]: formatMessage({ id: 'sec-loans-property-type-right-of-occupancy' }),
    [LoanOfferTargetType.RightToRent]: formatMessage({ id: 'loan-offer-property-type-right-to-rent' }),
    [LoanOfferTargetType.Timeshare]: formatMessage({ id: 'loan-offer-property-type-time-share' }),
    [LoanOfferTargetType.Unknown]: formatMessage({ id: 'loan-offer-target-type-unknown' }),
  }

  const amortizationTypeTexts: Record<LoanOfferAmortization, string> = {
    [LoanOfferAmortization.Annuity]: formatMessage({ id: 'sec-loans-payment-plan-method-of-repayment-annuity-label' }),
    [LoanOfferAmortization.Bullet]: formatMessage({ id: 'sec-loans-payment-plan-method-of-repayment-bullet-label' }),
    [LoanOfferAmortization.ConstantAmount]: formatMessage({
      id: 'sec-loans-payment-plan-method-of-repayment-equal-payment-label',
    }),
    [LoanOfferAmortization.ConstantDuration]: formatMessage({
      id: 'sec-loans-payment-plan-method-of-repayment-constant-duration-label',
    }),
  }

  const amortizationDescriptionTexts: Record<LoanOfferAmortization, string> = {
    [LoanOfferAmortization.Annuity]: formatMessage({
      id: 'sec-loans-payment-plan-method-of-repayment-annuity-description',
    }),
    [LoanOfferAmortization.Bullet]: formatMessage({
      id: 'sec-loans-payment-plan-method-of-repayment-bullet-description',
    }),
    [LoanOfferAmortization.ConstantAmount]: formatMessage({
      id: 'sec-loans-payment-plan-method-of-repayment-equal-payment-description',
    }),
    [LoanOfferAmortization.ConstantDuration]: formatMessage({
      id: 'sec-loans-payment-plan-method-of-repayment-constant-duration-description',
    }),
  }

  const loanPurposeTexts: Record<LoanOfferPurpose, string> = {
    [LoanOfferPurpose.Car]: formatMessage({ id: 'loan-offer-purpose-car-text' }),
    [LoanOfferPurpose.Consumption]: formatMessage({ id: 'loan-offer-purpose-consumption-text' }),
    [LoanOfferPurpose.Investment]: formatMessage({ id: 'loan-offer-purpose-investment-text' }),
    [LoanOfferPurpose.ObjectRelated]: formatMessage({ id: 'loan-offer-purpose-object-text' }),
    [LoanOfferPurpose.Other]: formatMessage({ id: 'common-other' }),
  }

  const financingTypeTexts: Record<Exclude<LoanOfferFinancingType, LoanOfferFinancingType.Regular>, string> = {
    [LoanOfferFinancingType.Asp]: formatMessage({ id: 'loan-offer-financing-type-asp' }),
    [LoanOfferFinancingType.Bridging]: formatMessage({ id: 'loan-offer-financing-type-bridging' }),
  }

  return {
    loanOfferTypeTexts,
    referenceRateTexts,
    targetTypeTexts,
    amortizationTypeTexts,
    amortizationDescriptionTexts,
    loanPurposeTexts,
    financingTypeTexts,
  }
}
